import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { Users, ShoppingBag, Flag, RefreshCw, TrendingUp, UserPlus } from 'lucide-react';
import { apiClient } from '@/lib/api-client';

function StatCard({ label, value, icon: Icon, to, color }: {
  label: string; value: number | string; icon: any; to: string; color: string;
}) {
  return (
    <Link to={to} className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-gray-500 dark:text-zinc-500 font-medium">{label}</span>
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
      <p className="text-3xl font-bold text-gray-900 dark:text-zinc-100">{value}</p>
    </Link>
  );
}

export default function AdminDashboardPage() {
  const { data, isLoading } = useQuery('admin-stats', () =>
    apiClient.get<any>('/admin/stats'),
    { staleTime: 0 }
  );

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-1">Dashboard</h1>
      <p className="text-sm text-gray-500 dark:text-zinc-500 mb-8">Platform overview</p>

      {isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="skeleton h-28 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          <StatCard label="Total Users"      value={data?.total_users ?? 0}     icon={Users}      to="/admin/users"    color="bg-blue-500" />
          <StatCard label="Active Listings"  value={data?.active_listings ?? 0} icon={ShoppingBag}to="/admin/listings" color="bg-green-500" />
          <StatCard label="Pending Trades"   value={data?.pending_trades ?? 0}  icon={RefreshCw}  to="/admin/listings" color="bg-purple-500" />
          <StatCard label="Total Orders"     value={data?.total_orders ?? 0}    icon={TrendingUp} to="/admin/listings" color="bg-orange-500" />
          <StatCard label="Pending Reports"  value={data?.pending_reports ?? 0} icon={Flag}       to="/admin/reports"  color="bg-red-500" />
          <StatCard label="New Users Today"  value={data?.new_users_today ?? 0} icon={UserPlus}   to="/admin/users"    color="bg-brand-500" />
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 p-5">
          <h2 className="font-semibold mb-4">Quick Actions</h2>
          <div className="space-y-2">
            {[
              { label: 'Review pending reports', to: '/admin/reports', badge: data?.pending_reports },
              { label: 'Manage users',           to: '/admin/users' },
              { label: 'Post an announcement',   to: '/admin/announcements' },
              { label: 'View audit log',         to: '/admin/audit-log' },
            ].map(({ label, to, badge }) => (
              <Link key={to} to={to} className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-gray-50 dark:bg-zinc-800 hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors text-sm font-medium">
                {label}
                {badge ? <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">{badge}</span> : null}
              </Link>
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-gray-200 dark:border-zinc-800 p-5">
          <h2 className="font-semibold mb-4">Platform Health</h2>
          <div className="space-y-3">
            {[
              { label: 'Pending Reports',  value: data?.pending_reports ?? 0,  ok: (data?.pending_reports ?? 0) < 10 },
              { label: 'Active Listings',  value: data?.active_listings ?? 0,  ok: true },
              { label: 'New Users Today',  value: data?.new_users_today ?? 0,  ok: true },
            ].map(({ label, value, ok }) => (
              <div key={label} className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-zinc-400">{label}</span>
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{value}</span>
                  <div className={`h-2 w-2 rounded-full ${ok ? 'bg-green-400' : 'bg-red-400'}`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
